import { useCallback } from "react";

import type { EcReceiptDetail_ManagementEcPageQuery, EcReceiptEmailResendInput } from "@/lib/gql/__generated__/graphql";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { useMarkVoucherAsUnUsed, useMarkVoucherAsUsed, useResendECReceiptEmail } from "../../repository/mutation";
import { convertQueryDataToViewData } from "../converter/query-data-to-view-data";

type Props = {
	hotelID: string;
	data: EcReceiptDetail_ManagementEcPageQuery;
};

export const useInitialized = ({ hotelID, data }: Props) => {
	const viewData = convertQueryDataToViewData({ queryData: data });

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { markVoucherAsUnUsed } = useMarkVoucherAsUnUsed();
	const { markVoucherAsUsed } = useMarkVoucherAsUsed();

	/**
	 * voucher の status を変更する
	 */
	const onMarkVoucher = useCallback(
		async (voucherCode: string, isUsed: boolean, receiptID: string) => {
			showLoadingScreenCallback();

			if (isUsed) {
				// 使用済みの場合は未使用にする
				const { errors } = await markVoucherAsUnUsed({ hotelID, voucherCode, receiptID }).finally(() =>
					hideLoadingScreenCallback(),
				);

				if (!errors) {
					notifyUserEventInfo({ title: "ステータスを変更しました" });
				}
			} else {
				// 未使用の場合は使用済みにする
				const { errors } = await markVoucherAsUsed({ hotelID, voucherCode, receiptID }).finally(() =>
					hideLoadingScreenCallback(),
				);

				if (!errors) {
					notifyUserEventInfo({ title: "ステータスを変更しました" });
				}
			}
		},
		[hideLoadingScreenCallback, hotelID, markVoucherAsUnUsed, markVoucherAsUsed, showLoadingScreenCallback],
	);

	const { resendECReceiptEmail } = useResendECReceiptEmail();

	/**
	 * EC レシートのメールを再送信する
	 */
	const onResendECReceiptEmail = useCallback(
		async (input: EcReceiptEmailResendInput) => {
			showLoadingScreenCallback();

			const { errors } = await resendECReceiptEmail(input).finally(() => hideLoadingScreenCallback());

			if (!errors) {
				notifyUserEventInfo({ title: "再送信しました" });
			}
		},
		[hideLoadingScreenCallback, resendECReceiptEmail, showLoadingScreenCallback],
	);

	return {
		//
		viewData,

		onMarkVoucher,
		onResendECReceiptEmail,
	};
};
